import { useMemo, useState } from 'react';
import { useList } from '@refinedev/core';
import { ChevronDown, Check } from 'lucide-react';
import { OrderTargetType } from '@masters-club/types';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Funnel tabs — primary categorization of Kanban by product type (targetType).
 *
 * Each product-type button is a Popover: clicking it opens a dropdown with
 * searchable list of concrete products of that type. Selecting a product sets
 * both targetType and targetId. Clicking the button when its type is already
 * active selects the first product or just applies the type filter.
 *
 * "Все" clears targetType + targetId.
 */

interface TranslationsByLang {
  ru?: { name?: string | null; title?: string | null };
  [lang: string]: { name?: string | null; title?: string | null } | undefined;
}

interface TranslationsArrayEntry {
  languageCode: string;
  name?: string | null;
  title?: string | null;
  data?: { name?: string; title?: string };
}

interface CourseOption {
  id: number;
  slug: string;
  translations?: TranslationsArrayEntry[];
}

interface SubscriptionPlanOption {
  id: number;
  code: string;
  translations?: TranslationsByLang;
}

interface ChannelPassOption {
  id: number;
  translations?: TranslationsByLang;
  channel?: { title: string } | null;
}

function resolveCourseLabel(item: CourseOption): string {
  const translations = item.translations ?? [];
  const ru = translations.find((t) => t.languageCode === 'ru');
  const ruTitle = ru?.title ?? ru?.data?.title;
  if (ruTitle) { return ruTitle; }
  const anyTitle = translations.find((t) => t.title ?? t.data?.title);
  if (anyTitle) { return anyTitle.title ?? anyTitle.data?.title ?? `#${item.id}`; }
  return item.slug ?? `#${item.id}`;
}

function pickAnyTranslationName(translations: TranslationsByLang | undefined): string | null {
  if (!translations) { return null; }
  for (const t of Object.values(translations)) {
    if (t?.name) { return t.name; }
  }
  return null;
}

function resolveSubscriptionPlanLabel(item: SubscriptionPlanOption): string {
  return item.translations?.ru?.name ?? pickAnyTranslationName(item.translations) ?? item.code ?? `#${item.id}`;
}

function resolveChannelPassLabel(item: ChannelPassOption): string {
  return item.translations?.ru?.name ?? pickAnyTranslationName(item.translations) ?? item.channel?.title ?? `#${item.id}`;
}

interface FunnelButtonProps {
  label: string;
  targetType: OrderTargetType;
  isActive: boolean;
  selectedTargetId: number | undefined;
  onSelect: (targetType: OrderTargetType, targetId: number | undefined) => void;
  /** Whether to show dropdown (false for BONUS — no admin listing available) */
  showDropdown: boolean;
  /** Pre-loaded options list + label resolver */
  options: Array<{ id: number; label: string }>;
  isLoading: boolean;
}

function FunnelButton({
  label,
  targetType,
  isActive,
  selectedTargetId,
  onSelect,
  showDropdown,
  options,
  isLoading,
}: FunnelButtonProps) {
  const [open, setOpen] = useState(false);
  const selectedLabel = selectedTargetId !== undefined
    ? options.find((o) => o.id === selectedTargetId)?.label
    : undefined;

  if (!showDropdown) {
    return (
      <Button
        type="button"
        size="sm"
        variant={isActive ? 'default' : 'ghost'}
        className={cn(
          'h-6 px-3 text-xs font-medium transition-colors',
          !isActive && 'hover:bg-background/80 text-muted-foreground',
        )}
        onClick={() => onSelect(targetType, undefined)}
      >
        {label}
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant={isActive ? 'default' : 'ghost'}
          className={cn(
            'h-6 px-3 text-xs font-medium transition-colors gap-1.5',
            !isActive && 'hover:bg-background/80 text-muted-foreground',
          )}
        >
          <span>{label}</span>
          {selectedLabel && (
            <Badge
              variant="secondary"
              className="max-w-[120px] truncate text-[10px] px-1 py-0 font-normal"
              title={selectedLabel}
            >
              {selectedLabel}
            </Badge>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Найти в «${label}»...`} className="h-9" />
          <CommandList>
            <CommandEmpty>{isLoading ? 'Загрузка...' : 'Ничего не найдено'}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onSelect(targetType, undefined);
                  setOpen(false);
                }}
                className="text-xs"
              >
                <Check className={cn('mr-2 h-3.5 w-3.5', selectedTargetId === undefined ? 'opacity-100' : 'opacity-0')} />
                Все в категории
              </CommandItem>
              {options.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.label}
                  onSelect={() => {
                    onSelect(targetType, opt.id);
                    setOpen(false);
                  }}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      selectedTargetId === opt.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface OrdersFunnelTabsProps {
  targetType: OrderTargetType | undefined;
  targetId: number | undefined;
  onChange: (next: { targetType: OrderTargetType | undefined; targetId: number | undefined }) => void;
}

export function OrdersFunnelTabs({ targetType, targetId, onChange }: OrdersFunnelTabsProps) {
  // Load all product catalogs upfront (cached 60s, only lists are cheap)
  const coursesResult = useList<CourseOption>({
    resource: 'courses',
    pagination: { pageSize: 200 },
    queryOptions: { staleTime: 60_000 },
  });
  const plansResult = useList<SubscriptionPlanOption>({
    resource: 'subscription-plans',
    pagination: { pageSize: 200 },
    queryOptions: { staleTime: 60_000 },
  });
  const passesResult = useList<ChannelPassOption>({
    resource: 'channel-passes',
    pagination: { pageSize: 200 },
    queryOptions: { staleTime: 60_000 },
  });

  const courseOptions = useMemo(
    () => (coursesResult.result?.data ?? []).map((c) => ({ id: c.id, label: resolveCourseLabel(c) })),
    [coursesResult.result],
  );
  const planOptions = useMemo(
    () => (plansResult.result?.data ?? []).map((p) => ({ id: p.id, label: resolveSubscriptionPlanLabel(p) })),
    [plansResult.result],
  );
  const passOptions = useMemo(
    () => (passesResult.result?.data ?? []).map((p) => ({ id: p.id, label: resolveChannelPassLabel(p) })),
    [passesResult.result],
  );

  const isAllActive = targetType === undefined;

  const handleSelect = (nextType: OrderTargetType, nextId: number | undefined): void => {
    onChange({ targetType: nextType, targetId: nextId });
  };

  const handleClearAll = (): void => {
    onChange({ targetType: undefined, targetId: undefined });
  };

  return (
    <div className="inline-flex h-8 items-center gap-1 rounded-lg bg-muted px-1">
      <Button
        type="button"
        size="sm"
        variant={isAllActive ? 'default' : 'ghost'}
        className={cn(
          'h-6 px-3 text-xs font-medium transition-colors',
          !isAllActive && 'hover:bg-background/80 text-muted-foreground',
        )}
        onClick={handleClearAll}
      >
        Все
      </Button>
      <FunnelButton
        label="Курсы"
        targetType={OrderTargetType.COURSE}
        isActive={targetType === OrderTargetType.COURSE}
        selectedTargetId={targetType === OrderTargetType.COURSE ? targetId : undefined}
        onSelect={handleSelect}
        showDropdown
        options={courseOptions}
        isLoading={coursesResult.query?.isLoading ?? false}
      />
      <FunnelButton
        label="Абонементы"
        targetType={OrderTargetType.SUBSCRIPTION_PLAN}
        isActive={targetType === OrderTargetType.SUBSCRIPTION_PLAN}
        selectedTargetId={targetType === OrderTargetType.SUBSCRIPTION_PLAN ? targetId : undefined}
        onSelect={handleSelect}
        showDropdown
        options={planOptions}
        isLoading={plansResult.query?.isLoading ?? false}
      />
      <FunnelButton
        label="Каналы"
        targetType={OrderTargetType.CHANNEL_PASS}
        isActive={targetType === OrderTargetType.CHANNEL_PASS}
        selectedTargetId={targetType === OrderTargetType.CHANNEL_PASS ? targetId : undefined}
        onSelect={handleSelect}
        showDropdown
        options={passOptions}
        isLoading={passesResult.query?.isLoading ?? false}
      />
      <FunnelButton
        label="Привилегии"
        targetType={OrderTargetType.BONUS}
        isActive={targetType === OrderTargetType.BONUS}
        selectedTargetId={undefined}
        onSelect={handleSelect}
        showDropdown={false}
        options={[]}
        isLoading={false}
      />
    </div>
  );
}
